import { useTranslation } from "react-i18next";
import type { Locale } from "@nh/shared";
import { TextField } from "@/shared/ui/core";
import { TextAreaField } from "@/features/admin/components";

export interface ProductContentFieldsProps {
  locale: Locale;
  /** El español es obligatorio; el inglés no. */
  required: boolean;
}

/**
 * El contenido del producto en UN idioma: nombre, descripción y leyenda.
 *
 * Los nombres de los campos son la ruta del contrato
 * (`translations.es.name`), no un nombre plano: así lo que el formulario
 * produce ya tiene la forma que el API espera, y cuando el contrato
 * rechaza algo el error aterriza en el campo exacto.
 *
 * Las ETIQUETAS van en el idioma de la interfaz, no en el del contenido:
 * quien administra en español escribiendo la ficha en inglés sigue
 * leyendo "Nombre", no "Name". El idioma que se está editando lo dice la
 * pestaña, que es donde se decidió.
 *
 * ── Dónde se fue el ESLOGAN ─────────────────────────────────────────
 * Anahí pidió tres campos aquí —nombre, descripción y leyenda— y no
 * mencionó el eslogan. NO se borró, y no se podía: `contentFor` lo
 * devuelve y la ficha de la tienda lo pinta bajo el nombre, así que
 * quitarlo del contrato habría vaciado ese renglón en cada producto que
 * lo tiene escrito, sin forma de recuperarlo.
 *
 * Se MUDÓ al paso de Beneficios, que es su sitio natural: el eslogan es
 * la promesa de una línea y los beneficios son la lista de promesas. Los
 * dos son contenido de venta, los dos son bilingües y los dos se
 * escriben mirándose entre sí. Aquí sobraba: este paso quedó para lo que
 * IDENTIFICA al producto —cómo se llama, qué es y qué hay que advertir—
 * y el eslogan es otra cosa.
 */
export function ProductContentFields({ locale, required }: ProductContentFieldsProps) {
  const { t } = useTranslation();

  return (
    <>
      <TextField
        name={`translations.${locale}.name`}
        label={t("Nombre del producto")}
        required={required}
        placeholder={t("Colágeno hidrolizado")}
      />

      {/*
        Los tres comparten fila. La descripción y la leyenda son párrafos
        y el nombre es un renglón, pero a un tercio del paso completo son
        ~350 px cada uno, que ya no es una rendija — y una fila suelta por
        campo costaba dos renglones de alto en un formulario que pelea por
        caber en una vista.
      */}
      <TextAreaField
        name={`translations.${locale}.description`}
        label={t("Descripción")}
        /*
          Dos renglones, no tres: comparte fila con un campo de UNA línea,
          así que cada renglón de más desnivela la fila y estira el paso.
          El textarea sigue pudiendo crecer tirando de su esquina.
        */
        rows={2}
        hint={t("Qué es, para qué sirve y cómo se toma.")}
      />

      {/*
        La leyenda es el campo NUEVO de este paso, y es traducible a
        propósito: un aviso legal en el idioma equivocado no avisa de
        nada. Convive con `legend` —el aviso de un solo idioma que ya
        existía, pensado como la letra pequeña del envase— y es este el
        que la ficha pública debe preferir.
      */}
      <TextAreaField
        name={`translations.${locale}.disclaimer`}
        label={t("Leyenda")}
        rows={2}
        hint={t("El aviso de la ficha: no es un medicamento, alérgenos, FDA.")}
      />
    </>
  );
}
